import { Vector3, Spherical, MathUtils } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { easeInOutCubic, easeOutQuart } from "./projections";
import {
    ZOOM_FAR,
    ZOOM_NEAR,
    PLAYING_PRESETS,
    CAMERA_DURATION,
    AUTO_ROTATE_SPEED,
    ACHIEVEMENT_AUTO_ROTATE_SPEED,
    LANDING_POS,
    LANDING_LOOK_AT,
    COUNTDOWN_POS,
    COUNTDOWN_LOOK_AT,
    COUNTDOWN_MOBILE_POS,
    COUNTDOWN_MOBILE_LOOK_AT,
    IDLE_RESUME_DELAY,
} from "./config";
const LANDING_SPEED = (2 * Math.abs(AUTO_ROTATE_SPEED)) / 60;

const _tmpLookAt = new Vector3();
const _lerpSph = new Spherical();
const _up = new Vector3(0, 1, 0);
const _camRight = new Vector3();
const _camUp_f = new Vector3();
const _savedPos = new Vector3();

export default class CameraRig {
    constructor() {
        this.engine = null;
        this.controls = null;

        this._mode = "landing";
        this._transitioning = false;
        this._transTime = 0;
        this._startSph = new Spherical();
        this._targetSph = new Spherical();

        this._autoRotateEnabled = true;
        this._currentSpeed = 0;
        this._idleTimer = 0;
        this._mouseHovering = false;

        this._zoomAccum = 0;

        this._playingPos = PLAYING_PRESETS.default.pos.clone();
        this._playingLookAt = PLAYING_PRESETS.default.lookAt.clone();
        this._controlsLocked = false;
        this._startLookAt = new Vector3();
        this._endLookAt = new Vector3();
        this._activeLandingPos = LANDING_POS.clone();
        this._activeLandingLookAt = LANDING_LOOK_AT.clone();
        this._landingHorizR = Math.sqrt(
            LANDING_POS.x ** 2 + LANDING_POS.z ** 2,
        );
        this._isCountdown = false;
        this._isMobileCache = false;

        this._currentLookAt = LANDING_LOOK_AT.clone();

        this._landingAngle = 0;

        this._focusLocked = false;
        this._focusReturning = false;
        this._focusTransitioning = false;
        this._focusTime = 0;
        this._focusStartPos = new Vector3();
        this._focusTargetPos = new Vector3();
        this._focusStartLookAt = new Vector3();
        this._focusLookAt = new Vector3();
        this._preFocusPos = new Vector3();

        this._distanceMult = 1;
        this._verticalFocusOffset = 0;

        this._entranceYOffset = 8;
        this._entranceStartOffset = 8;
        this._entranceTime = null;
        this._onEntranceComplete = null;

        this._yRotAllowed = false;
        this._pendingYRot = false;
        this._yRotAngle = 0;
        this._yRotDelta = 0;
        this._yRotDragging = false;
        this._yRotLastX = 0;
        this._yRotSensitivity = 0.0005;
        this._yRotDamping = 0.04;
        this._yRotBaseCamPos = new Vector3();
        this._yRotBaseLookAt = new Vector3();
    }

    onAdd(engine) {
        this.engine = engine;

        engine.camera.position.copy(this._activeLandingPos);
        engine.camera.lookAt(this._activeLandingLookAt);
        this._currentLookAt.copy(this._activeLandingLookAt);

        const controls = new OrbitControls(engine.camera, engine.DOM.canvas);
        this.controls = controls;

        controls.enableDamping = true;
        controls.dampingFactor = 0.04;
        controls.enablePan = false;
        controls.enableRotate = true;
        controls.autoRotate = false;
        controls.autoRotateSpeed = AUTO_ROTATE_SPEED;
        controls.mouseButtons = { LEFT: 0, MIDDLE: 2, RIGHT: 2 };
        controls.enabled = false;
        controls.minDistance = ZOOM_NEAR;
        controls.maxDistance = ZOOM_FAR * 1.2;

        this._startSph.setFromVector3(this._activeLandingPos);

        const canvas = engine.DOM.canvas;
        canvas.addEventListener("pointerdown", this._onPointerDown);
        canvas.addEventListener("pointermove", this._onPointerMove);
        canvas.addEventListener("pointerup", this._onPointerUp);
        canvas.addEventListener("pointercancel", this._onPointerUp);

        this.resize([], this.engine.isMobile);
    }

    update(delta) {
        if (!this.engine) return;

        const d = Math.min(delta, 0.05);

        this._stepEntrance(d);
        this._stepFocusTransition(d);
        this._stepModeTransition(d);
        this._stepLandingRotate(d);
        this._stepAutoRotate(d);
        this._stepZoom();
        this._stepAchievementRotate(d);

        if (this.controls?.enabled) {
            this.controls.update();
        }
    }

    resize = (sizes, isMobile) => {
        const newMult = isMobile ? 0.95 : 1;

        if (newMult !== this._distanceMult) {
            const ratio = newMult / this._distanceMult;

            this._distanceMult = newMult;
            this._verticalFocusOffset = isMobile ? 0.4 : 0;

            if (this.controls) {
                this.controls.minDistance = ZOOM_NEAR * this._distanceMult;
                this.controls.maxDistance = ZOOM_FAR * 1.2 * this._distanceMult;
            }

            if (this._focusLocked && this.engine) {
                this._focusTargetPos.multiplyScalar(ratio);

                this._focusLookAt.set(0, 0, 0);
                if (this._verticalFocusOffset !== 0) {
                    const focalDir = _camUp_f
                        .copy(this._focusTargetPos)
                        .normalize();
                    _camRight.crossVectors(_up, focalDir);
                    if (_camRight.lengthSq() > 0.001) {
                        _camRight.normalize();
                        _camUp_f.crossVectors(focalDir, _camRight).normalize();
                        this._focusLookAt.addScaledVector(
                            _camUp_f,
                            -this._verticalFocusOffset,
                        );
                    }
                }

                this._focusStartPos.copy(this.engine.camera.position);
                this._focusStartLookAt.copy(this._currentLookAt);
                this._focusTime = 0;
                this._focusReturning = false;
                this._focusTransitioning = true;
            }
        }

        this.setLandingVariant(this._isCountdown, isMobile);
    };

    dispose() {
        const canvas = this.engine?.DOM.canvas;
        if (canvas) {
            canvas.removeEventListener("pointerdown", this._onPointerDown);
            canvas.removeEventListener("pointermove", this._onPointerMove);
            canvas.removeEventListener("pointerup", this._onPointerUp);
            canvas.removeEventListener("pointercancel", this._onPointerUp);
        }
        this.controls?.dispose();
        this.controls = null;
        this.engine = null;
    }

    setLandingVariant(isCountdown, isMobile) {
        if (
            isCountdown === this._isCountdown &&
            isMobile === this._isMobileCache
        )
            return;

        this._isCountdown = isCountdown;
        this._isMobileCache = isMobile;

        const pos = isCountdown
            ? isMobile
                ? COUNTDOWN_MOBILE_POS
                : COUNTDOWN_POS
            : LANDING_POS;
        const lookAt = isCountdown
            ? isMobile
                ? COUNTDOWN_MOBILE_LOOK_AT
                : COUNTDOWN_LOOK_AT
            : LANDING_LOOK_AT;

        this._activeLandingPos.copy(pos);
        this._activeLandingLookAt.copy(lookAt);
        this._landingHorizR = Math.sqrt(pos.x ** 2 + pos.z ** 2);

        if (this._mode === "landing" && !this._transitioning && this.engine) {
            const cam = this.engine.camera;
            cam.position.set(
                Math.sin(this._landingAngle) * this._landingHorizR,
                pos.y,
                Math.cos(this._landingAngle) * this._landingHorizR,
            );
            cam.lookAt(lookAt);
            this._currentLookAt.copy(lookAt);
        }
    }

    transitionToPlaying() {
        if (this._mode === "playing") return;

        if (this._playingLookAt.lengthSq() < 0.001) {
            const r = this._playingPos.length();
            this._playingPos.set(
                Math.sin(this._landingAngle) * r,
                this._playingPos.y,
                Math.cos(this._landingAngle) * r,
            );
        }

        this._beginTransition("playing");
    }

    cancelFocus() {
        this._focusLocked = false;
        this._focusReturning = false;
        this._focusTransitioning = false;
    }

    setPlayingPreset(name) {
        const preset = this.engine.isMobile
            ? (PLAYING_PRESETS[`${name}Mobile`] ?? PLAYING_PRESETS.default)
            : (PLAYING_PRESETS[name] ?? PLAYING_PRESETS.default);

        this._playingPos.copy(preset.pos).multiplyScalar(this._distanceMult);
        this._playingLookAt.copy(preset.lookAt);
        this._controlsLocked = preset.lockControls;
        this._pendingYRot = preset.yRotation ?? false;
        if (!this._pendingYRot) {
            this._yRotAllowed = false;
            this._yRotAngle = 0;
            this._yRotDelta = 0;
            this._yRotDragging = false;
            if (this.engine) this.engine.DOM.canvas.style.cursor = "";
        }

        if (this._mode !== "playing") return;

        this._transTime = 0;
        this._transitioning = true;
        this._startSph.setFromVector3(this.engine.camera.position);
        this._targetSph.setFromVector3(this._playingPos);
        this._startLookAt.copy(this._currentLookAt);
        this._endLookAt.copy(this._playingLookAt);

        if (this.controls) this.controls.enabled = false;
    }

    transitionToLanding() {
        if (this._mode === "landing") return;

        this.cancelFocus();
        this.controls.enabled = false;

        const pos = this.engine.camera.position;
        this._landingAngle = Math.atan2(pos.x, pos.z);

        const landingTarget = new Vector3(
            Math.sin(this._landingAngle) * this._landingHorizR,
            this._activeLandingPos.y,
            Math.cos(this._landingAngle) * this._landingHorizR,
        );

        this._mode = "landing";
        this._transTime = 0;
        this._transitioning = true;
        this._startSph.setFromVector3(pos);
        this._targetSph.setFromVector3(landingTarget);
        this._startLookAt.copy(this._currentLookAt);
        this._endLookAt.copy(this._activeLandingLookAt);
    }

    focusOnLatLng(lat, lng, distance = ZOOM_NEAR) {
        this._transitioning = false;
        const toRad = Math.PI / 180;
        const x = Math.cos(lat * toRad) * Math.cos(lng * toRad);
        const y = Math.sin(lat * toRad);
        const z = Math.cos(lat * toRad) * Math.sin(lng * toRad);

        if (!this._focusLocked) {
            this._preFocusPos.copy(this.engine.camera.position);
        }
        this._focusStartPos.copy(this.engine.camera.position);
        this._focusTargetPos
            .set(x, y, -z)
            .normalize()
            .multiplyScalar(distance * this._distanceMult);

        this._focusStartLookAt.copy(this._currentLookAt);
        this._focusLookAt.set(0, 0, 0);
        if (this._verticalFocusOffset !== 0) {
            const focalDir = _tmpLookAt.set(x, y, -z).normalize();
            _camRight.crossVectors(_up, focalDir);
            if (_camRight.lengthSq() > 0.001) {
                _camRight.normalize();
                _camUp_f.crossVectors(focalDir, _camRight).normalize();
                this._focusLookAt.addScaledVector(
                    _camUp_f,
                    -this._verticalFocusOffset,
                );
            }
        }

        this._focusTime = 0;
        this._focusLocked = true;
        this._focusReturning = false;
        this._focusTransitioning = true;
        this.controls.enabled = false;
    }

    clearFocus() {
        if (!this._focusLocked) return;
        this._focusStartPos.copy(this.engine.camera.position);
        this._focusTargetPos
            .copy(this.engine.camera.position)
            .normalize()
            .multiplyScalar(this._playingPos.length());
        this._focusStartLookAt.copy(this._currentLookAt);
        this._focusLookAt.set(0, 0, 0);
        this._focusTime = 0;
        this._focusReturning = true;
        this._focusTransitioning = true;
    }

    startEntrance(onComplete) {
        this._entranceStartOffset = this._entranceYOffset;
        this._entranceTime = -0.3;
        this._onEntranceComplete = onComplete ?? null;
    }

    _stepEntrance(delta) {
        if (this._entranceTime === null) return;

        this._entranceTime += delta;

        if (this._entranceTime < 0) return;

        const duration = 2.0;
        const t = easeOutQuart(Math.min(this._entranceTime / duration, 1));
        this._entranceYOffset = this._entranceStartOffset * (1 - t);

        if (this._entranceTime >= duration) {
            this._entranceYOffset = 0;
            this._entranceTime = null;
            const cb = this._onEntranceComplete;
            this._onEntranceComplete = null;
            cb?.();
        }
    }

    setAutoRotate(enabled) {
        this._autoRotateEnabled = enabled;
    }

    unlockControlsInPlace() {
        if (this._mode !== "playing") return;
        this._controlsLocked = false;
        this._transitioning = false;
        if (this.controls) {
            this.controls.target.set(0, 0, 0);
            this._currentSpeed = 0;
            this._resetControlsVelocity();
            this.controls.enabled = true;
        }
    }

    enableControlsWithoutTransition() {
        if (this._mode !== "playing") return;

        const preset = PLAYING_PRESETS.default;
        this._playingPos.copy(preset.pos).multiplyScalar(this._distanceMult);
        this._playingLookAt.copy(preset.lookAt);
        this._controlsLocked = false;
        this._transitioning = false;
        this.cancelFocus();

        if (this.controls) {
            this.controls.target.set(0, 0, 0);
            this._currentSpeed = 0;
            this._resetControlsVelocity();
            this.controls.enabled = true;
        }
    }

    _resetControlsVelocity() {
        if (!this.controls) return;
        const cam = this.engine.camera;
        _savedPos.copy(cam.position);
        const prev = this.controls.dampingFactor;
        this.controls.autoRotate = false;
        this.controls.dampingFactor = 1;
        this.controls.update();
        this.controls.dampingFactor = prev;
        cam.position.copy(_savedPos);
        cam.lookAt(this._currentLookAt);
    }

    zoom(direction) {
        this._zoomAccum += direction;
    }

    setMouseHovering(hovering) {
        if (hovering) this._idleTimer = 0;

        this._mouseHovering = hovering;
    }

    _beginTransition(mode) {
        this._mode = mode;
        this._transTime = 0;
        this._transitioning = true;

        this._startSph.setFromVector3(this.engine.camera.position);
        this._targetSph.setFromVector3(
            mode === "playing" ? this._playingPos : this._activeLandingPos,
        );
        this._startLookAt.copy(this._currentLookAt);
        this._endLookAt.copy(
            mode === "playing"
                ? this._playingLookAt
                : this._activeLandingLookAt,
        );
    }

    _stepFocusTransition(delta) {
        if (!this._focusTransitioning) return;

        this._focusTime = Math.min(this._focusTime + delta, CAMERA_DURATION);
        const t = easeInOutCubic(this._focusTime / CAMERA_DURATION);

        this.engine.camera.position.lerpVectors(
            this._focusStartPos,
            this._focusTargetPos,
            t,
        );
        _tmpLookAt.lerpVectors(this._focusStartLookAt, this._focusLookAt, t);
        this.engine.camera.lookAt(_tmpLookAt);
        this._currentLookAt.copy(_tmpLookAt);

        if (this._focusTime >= CAMERA_DURATION) {
            this._focusTransitioning = false;

            if (this._focusReturning) {
                this._focusReturning = false;
                this._focusLocked = false;
                this._currentSpeed = 0;
                if (this.controls) this.controls.target.set(0, 0, 0);
                this._resetControlsVelocity();
                this.controls.enabled = true;
            }
        }
    }

    _stepModeTransition(delta) {
        if (!this._transitioning) return;

        const cam = this.engine.camera;

        this._transTime = Math.min(this._transTime + delta, CAMERA_DURATION);
        const raw = this._transTime / CAMERA_DURATION;
        const t = easeInOutCubic(raw);

        const s = this._startSph;
        const e = this._targetSph;

        _lerpSph.set(
            MathUtils.lerp(s.radius, e.radius, t),
            MathUtils.lerp(s.phi, e.phi, t),
            MathUtils.lerp(s.theta, e.theta, t),
        );
        cam.position.setFromSpherical(_lerpSph);
        _tmpLookAt.lerpVectors(this._startLookAt, this._endLookAt, t);
        cam.lookAt(_tmpLookAt);
        this._currentLookAt.copy(_tmpLookAt);

        if (raw >= 1) {
            if (this._mode === "playing") {
                cam.position.copy(this._playingPos);
            } else {
                cam.position.setFromSpherical(this._targetSph);
            }
            const finalLookAt =
                this._mode === "playing"
                    ? this._playingLookAt
                    : this._activeLandingLookAt;
            cam.lookAt(finalLookAt);
            this._currentLookAt.copy(finalLookAt);
            this._transitioning = false;

            if (this._mode === "playing") {
                if (this.controls) {
                    this.controls.target.copy(finalLookAt);
                    this._currentSpeed = 0;
                    this._resetControlsVelocity();
                    this.controls.enabled = !this._controlsLocked;
                }
                if (this._pendingYRot) {
                    this._yRotBaseCamPos.copy(this._playingPos);
                    this._yRotBaseLookAt.copy(this._playingLookAt);
                    this._yRotAngle = 0;
                    this._yRotAllowed = true;
                    this._pendingYRot = false;
                    this.engine.DOM.canvas.style.cursor = "grab";
                }
            }
        }
    }

    _stepLandingRotate(delta) {
        if (this._mode !== "landing" || this._transitioning) return;

        this._landingAngle -=
            LANDING_SPEED * delta * (this._isCountdown ? 0.75 : 1);

        const cosA = Math.cos(this._landingAngle);
        const sinA = Math.sin(this._landingAngle);
        const cam = this.engine.camera;
        cam.position.set(
            sinA * this._landingHorizR,
            this._activeLandingPos.y + this._entranceYOffset,
            cosA * this._landingHorizR,
        );

        if (this._isCountdown) {
            const lx = this._activeLandingLookAt.x;
            const lz = this._activeLandingLookAt.z;
            _tmpLookAt.set(
                lx * cosA + lz * sinA,
                this._activeLandingLookAt.y + this._entranceYOffset,
                -lx * sinA + lz * cosA,
            );
        } else {
            _tmpLookAt.copy(this._activeLandingLookAt);
            _tmpLookAt.y += this._entranceYOffset;
        }

        cam.lookAt(_tmpLookAt);
        this._currentLookAt.copy(_tmpLookAt);
    }

    _stepAutoRotate(delta) {
        if (this._focusLocked) return;
        if (!this.controls?.enabled) return;

        if (this._mouseHovering) {
            this._idleTimer = 0;
        } else {
            this._idleTimer += delta;
        }

        const wantRotate =
            this._autoRotateEnabled && this._idleTimer >= IDLE_RESUME_DELAY;

        const targetSpeed = wantRotate ? AUTO_ROTATE_SPEED : 0;
        const isAccelerating =
            Math.abs(targetSpeed) > Math.abs(this._currentSpeed);
        const lerpK = isAccelerating ? 0.3 : 3;

        this._currentSpeed +=
            (targetSpeed - this._currentSpeed) * delta * lerpK;

        if (targetSpeed === 0 && Math.abs(this._currentSpeed) < 0.001) {
            this._currentSpeed = 0;
        }

        this.controls.autoRotate = this._currentSpeed !== 0;
        this.controls.autoRotateSpeed = this._currentSpeed;
    }

    _stepZoom() {
        if (this._focusLocked) return;
        if (!this.controls || Math.abs(this._zoomAccum) < 0.01) return;

        const cam = this.engine.camera;
        const c = this.controls;

        const offset = cam.position.clone().sub(c.target);
        const dist = offset.length();

        const newDist = MathUtils.clamp(
            dist + this._zoomAccum * 0.15,
            c.minDistance ?? 2,
            c.maxDistance ?? 10,
        );

        offset.normalize().multiplyScalar(newDist);
        cam.position.copy(c.target).add(offset);

        this._zoomAccum *= 0.88;
        if (Math.abs(this._zoomAccum) < 0.01) this._zoomAccum = 0;
    }

    _onPointerDown = (e) => {
        if (!this._yRotAllowed || this._transitioning) return;
        this._yRotDragging = true;
        this._yRotLastX = e.clientX;
        this._yRotDelta = 0;
        this.engine.DOM.canvas.style.cursor = "grabbing";
    };

    _onPointerMove = (e) => {
        if (!this._yRotDragging) return;
        const dx = e.clientX - this._yRotLastX;
        this._yRotLastX = e.clientX;
        this._yRotDelta += dx * this._yRotSensitivity;
    };

    _onPointerUp = () => {
        if (this._yRotDragging) {
            this._yRotDragging = false;
            if (this._yRotAllowed) this.engine.DOM.canvas.style.cursor = "grab";
        }
    };

    _stepAchievementRotate(delta) {
        if (!this._yRotAllowed || this._transitioning) return;

        this._yRotAngle += this._yRotDelta;
        this._yRotDelta *= Math.pow(1 - this._yRotDamping, delta * 60);
        if (Math.abs(this._yRotDelta) < 0.000001) this._yRotDelta = 0;

        if (this._autoRotateEnabled) {
            this._yRotAngle += ACHIEVEMENT_AUTO_ROTATE_SPEED * delta;
        }

        this._applyYRotation();
    }

    _applyYRotation() {
        if (!this.engine) return;
        const cos = Math.cos(this._yRotAngle);
        const sin = Math.sin(this._yRotAngle);
        const bp = this._yRotBaseCamPos;
        const bl = this._yRotBaseLookAt;
        const cam = this.engine.camera;
        cam.position.set(
            bp.x * cos - bp.z * sin,
            bp.y,
            bp.x * sin + bp.z * cos,
        );
        _tmpLookAt.set(bl.x * cos - bl.z * sin, bl.y, bl.x * sin + bl.z * cos);
        cam.lookAt(_tmpLookAt);
        this._currentLookAt.copy(_tmpLookAt);
    }
}
